/**
 * Instance entry — resolve latest run + conversation (or create) and open the workspace.
 */

import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import {
  InstanceNotRunnableError,
  InstanceSetupRequiredError,
  resolveInstanceEntry,
} from '@/components/ai-studio/instances/resolveInstanceEntry';

const AIStudioInstanceEntryPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { instanceId = '' } = useParams<{ instanceId: string }>();
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(() => {
    if (!instanceId) {
      setErrorKey('aiStudio.instances.notFound');
      return;
    }

    let cancelled = false;
    void resolveInstanceEntry(instanceId)
      .then(({ runId, conversationId }) => {
        if (cancelled) return;
        const params = new URLSearchParams({
          conversationId,
          instanceId,
        });
        navigate(`/ai-studio/runs/${encodeURIComponent(runId)}?${params.toString()}`, {
          replace: true,
        });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (error instanceof InstanceSetupRequiredError) {
          navigate(`/ai-studio/instances/${encodeURIComponent(instanceId)}/provision`, {
            replace: true,
          });
          return;
        }
        if (error instanceof InstanceNotRunnableError) {
          setErrorKey(
            error.reason === 'inactive'
              ? 'aiStudio.instances.notRunnableInactive'
              : error.reason === 'archived'
                ? 'aiStudio.instances.notRunnableArchived'
                : 'aiStudio.instances.setupRequired'
          );
          return;
        }
        setErrorKey('aiStudio.instances.loadFailed');
      });

    return () => {
      cancelled = true;
    };
  }, [instanceId, navigate]);

  if (errorKey) {
    return (
      <main className="flex flex-col gap-3 px-4 py-4 md:px-6 lg:px-8">
        <p role="alert" className="text-base text-[var(--color-error)]">
          {t(errorKey)}
        </p>
        <Link
          to="/ai-studio/instances"
          className="w-fit text-base text-[var(--color-info)] underline"
        >
          {t('aiStudio.instances.backToCatalog')}
        </Link>
      </main>
    );
  }

  return (
    <main className="px-4 py-4 md:px-6 lg:px-8">
      <p className="text-base text-[var(--color-surface-text-secondary)]">
        {t('aiStudio.instances.opening')}
      </p>
    </main>
  );
};

export default AIStudioInstanceEntryPage;
